import axios from "axios";
import { decryptData, encryptData } from "./decryption";
import { toast } from "react-toastify";
import { getSyncedTimestampSec, updateOffsetFromResponse } from "./timeSync";
import { createHmacSignature } from "./hmac";


// const REACT_APP_API_BASE_URL = "https://bmcedu-server.vercel.app/api/v2";
// const REACT_APP_API_BASE_URL = "http://localhost:4000/api/v2";
const REACT_APP_API_BASE_URL = "https://api.bmc.io.vn/api/v2";

// export const HOSTNAME = "http://localhost:4000";
export const HOSTNAME = "https://api.bmc.io.vn";
export const URL_FORM = "https://bmc.io.vn";
// Tạo instance Axios
const apiClient = axios.create({
  baseURL: REACT_APP_API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let refreshPromise = null;
let isLoggingOut = false;

function logoutOnce(message) {
  if (isLoggingOut) return;
  isLoggingOut = true;

  if (message) toast.error(message);

  const currentPath = window.location.pathname + window.location.search;
  if (!currentPath.includes("/login") && currentPath !== "/") {
    sessionStorage.setItem("redirectAfterLogin", currentPath);
  }

  localStorage.removeItem("user");

  if (!window.location.pathname.includes("/login")) {
    window.location.href = "/login";
  }
}

apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    const skipEncryptRoutes = [
      "/upload/image",
      "/upload/file",
      "/auth/refresh",
    ];
    const shouldSkipEncrypt = skipEncryptRoutes.some((r) =>
      config.url?.includes(r),
    );

    const isFormData =
      typeof FormData !== "undefined" && config.data instanceof FormData;

    // giữ body gốc để retry POST sau refresh
    if (
      !shouldSkipEncrypt &&
      !isFormData &&
      config.data &&
      typeof config.data === "object" &&
      !Object.prototype.hasOwnProperty.call(config, "__rawData")
    ) {
      config.__rawData = config.data;
    }

    let finalBody = config.data;

    if (
      !shouldSkipEncrypt &&
      !isFormData &&
      config.data &&
      typeof config.data === "object"
    ) {
      finalBody = encryptData(config.__rawData ?? config.data);
      config.data = finalBody;
    }

    const timestamp = getSyncedTimestampSec();
    const clientId = process.env.REACT_APP_HMAC_CLIENT_ID;
    const secret = process.env.REACT_APP_HMAC_SECRET;

    // pathname ở đây đã là /api/v2/... rồi
    const pathname = new URL(config.url, config.baseURL).pathname;

    const fullPath = `/api/v2${pathname}`;
    const signature = createHmacSignature({
      method: config.method,
      url: fullPath,
      timestamp,
      body: finalBody,
      secret,
    });

    config.headers["x-client-id"] = clientId;
    config.headers["x-timestamp"] = timestamp;
    config.headers["x-signature"] = signature;

    return config;
  },
  (error) => Promise.reject(error),
);

function buildRetryConfig(cfg) {
  const nextCfg = {
    ...cfg,
    __retry: true,
    headers: {
      ...(cfg?.headers || {}),
    },
  };

  // Khôi phục body gốc để encrypt + sign lại đúng như request đầu
  if (Object.prototype.hasOwnProperty.call(nextCfg, "__rawData")) {
    nextCfg.data = nextCfg.__rawData;
  }

  delete nextCfg.headers["x-client-id"];
  delete nextCfg.headers["x-timestamp"];
  delete nextCfg.headers["x-signature"];
  delete nextCfg.headers.Authorization;
  delete nextCfg.headers.authorization;

  // KHÔNG xóa __retry
  delete nextCfg.__isRefreshing;

  return nextCfg;
}

async function refreshAccessToken(cfg) {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post(
        "/auth/refresh",
        {}, // dùng object rỗng, không dùng null
        {
          __skipAuthRefresh: true,
          __rawData: {},
          headers: {
            ...(cfg?.headers || {}),
          },
        },
      )
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => {
    updateOffsetFromResponse(response);
    return decryptData(response.data);
  },
  async (error) => {
    try {
      if (error?.response?.data) {
        error.response.data = decryptData(error.response.data);
      }
    } catch (e) {
      console.error("Decrypt error response failed:", e);
    }

    const res = error?.response;
    const cfg = error?.config || {};

    if (!res) return Promise.reject(error);

    updateOffsetFromResponse(res);

    const status = res.status;
    const errorCode = res.data?.errorCode;
    const message = String(res.data?.message || "");
    const isRefreshRequest = cfg?.url?.includes("/auth/refresh");

    const shouldTryRefresh =
      status === 401 &&
      !isRefreshRequest &&
      !cfg.__retry &&
      errorCode !== "REFRESH_TOKEN_INVALID" &&
      errorCode !== "REFRESH_TOKEN_MISSING";

    if (shouldTryRefresh) {
      try {
        await refreshAccessToken(cfg);
        return apiClient(buildRetryConfig(cfg));
      } catch (refreshError) {
        logoutOnce(message || "Phiên đăng nhập đã hết hạn");
        return Promise.reject(refreshError);
      }
    }

    if (status === 401) {
      logoutOnce(message || "Phiên đăng nhập đã hết hạn");
    }

    return Promise.reject(error);
  },
);


// Các hàm HTTP tiện dụng
export const get = (url, config = {}) => apiClient.get(url, config);
export const post = (url, data, config = {}) =>
  apiClient.post(url, data, config);
export const put = (url, data, config = {}) => apiClient.put(url, data, config);
export const del = (url, config = {}) => apiClient.delete(url, config);
export const patch = (url, data, config = {}) =>
  apiClient.patch(url, data, config);

export default apiClient;